import React from "react";
import { Box, Typography, Grid, Paper, Stack, Chip } from "@mui/material";
import { useSchemes } from "../../../../contexts/SchemesContext";
import {
  DefensivePlayLog,
  DefensivePlayCall,
  PenaltyLog,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { PenaltyLogDetails } from "../Components/PenaltyLogDetails";

interface DefenseLogDetailsProps {
  playCall: DefensivePlayCall;
  playLog: DefensivePlayLog;
  penalties: PenaltyLog[];
}

export const DefenseLogDetails: React.FC<DefenseLogDetailsProps> = ({
  playCall,
  playLog,
  penalties,
}) => {
  const { defensivePersonnel, fronts, stunts, coverages } = useSchemes();

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        
        {/* Play Call Section */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {playCall?.personnel_group_name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Front
                </Typography>
                <Typography variant="h6">
                  {playCall?.front_name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Stunt
                </Typography>
                <Typography variant="h6">
                  {playCall?.stunt_name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Coverage
                </Typography>
                <Typography variant="h6">
                  {playCall?.coverage_name || ""}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Gain/Loss
                </Typography>
                <Typography
                  variant="h5"
                  color={
                    playLog && playLog.gain_loss >= 0
                      ? "error.main"
                      : "success.main"
                  }
                >
                  {playLog ? (
                    <>
                      {playLog.gain_loss > 0 ? "+" : ""}
                      {playLog.gain_loss} yards
                    </>
                  ) : (
                    "0 yards"
                  )}
                </Typography>
              </Box>

              {playLog && (
                <Stack spacing={1}>
                  {/* Play Type */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Play Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {playLog.run_pass === "R" ? "Run" : "Pass"}
                    </Typography>
                  </Box>

                  {/* Formation */}
                  {playLog.offensive_formation && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Offensive Formation
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {playLog.offensive_formation}
                      </Typography>
                    </Box>
                  )}

                  {/* Tacklers */}
                  {playLog.tackle?.primary_tackler_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tackler
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(
                          playLog.tackle.primary_tackler_roster_player
                        )}
                      </Typography>
                    </Box>
                  )}

                  {playLog.tackle?.assisted_tacklers_roster_players &&
                    playLog.tackle.assisted_tacklers_roster_players.length >
                      0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Assisted By
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          gap={1}
                        >
                          {playLog.tackle.assisted_tacklers_roster_players.map(
                            (player, index) => (
                              <Chip
                                key={index}
                                label={jerseyNumberNameDisplay(player)}
                                size="small"
                                variant="outlined"
                              />
                            )
                          )}
                        </Stack>
                      </Box>
                    )}

                  {/* Pass Break Up */}
                  {playLog.pass_break_up_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pass Break Up
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(
                          playLog.pass_break_up_roster_player
                        )}
                      </Typography>
                    </Box>
                  )}

                  {/* Turnover Section */}
                  {(playLog.fumble || playLog.interception) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        borderRadius: 1,
                      }}
                    >
                      {playLog.fumble && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            FUMBLE RECOVERY
                          </Typography>
                          {playLog.fumble.fumble_recovery_roster_player && (
                            <Typography variant="body2">
                              Recovered by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.fumble.fumble_recovery_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                      {playLog.interception && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            INTERCEPTION
                          </Typography>
                          {playLog.interception.interception_roster_player && (
                            <Typography variant="body2">
                              Intercepted by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.interception.interception_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Tackle Section */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Tackle
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Primary Tackler
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 500 }}>
                  {playLog.tackle?.primary_tackler_roster_player
                    ? jerseyNumberNameDisplay(
                        playLog.tackle.primary_tackler_roster_player
                      )
                    : "None"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Assisted Tacklers
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 500 }}>
                  {playLog.tackle?.assisted_tacklers_roster_players
                    ? playLog.tackle.assisted_tacklers_roster_players.map(
                        (player) => jerseyNumberNameDisplay(player) + " "
                      )
                    : "None"}
                </Typography>
              </Box>

              {playLog && (
                <Stack spacing={1}>
                  {/* Turnover Section */}
                  {(playLog.fumble || playLog.interception) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        borderRadius: 1,
                      }}
                    >
                      {playLog.fumble && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            FUMBLE RECOVERY
                          </Typography>
                          {playLog.fumble.fumble_recovery_roster_player && (
                            <Typography variant="body2">
                              Recovered by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.fumble.fumble_recovery_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                      {playLog.interception && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            INTERCEPTION
                          </Typography>
                          {playLog.interception.interception_roster_player && (
                            <Typography variant="body2">
                              Intercepted by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.interception.interception_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Penalties Section */}
        <Grid item xs={12} md={3}>
          <PenaltyLogDetails penalties={penalties} />
        </Grid>
      </Grid>

    </Box>
  );
};
